<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { messageStamp } from 'shared/helpers/timeHelper';
import {
  isSafeHttpLink,
  getDocumentDisplayPath,
} from 'shared/helpers/documentHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import ResponseCard from 'dashboard/components-next/assistant/shared/ResponseCard.vue';
import DeleteDialog from 'dashboard/components-next/assistant/pageComponents/DeleteDialog.vue';
import CreateKnowledgeBaseDialog from 'dashboard/components-next/assistant/pageComponents/knowledgeBase/CreateKnowledgeBaseDialog.vue';
import AddKnowledgeDocumentDialog from 'dashboard/components-next/assistant/pageComponents/knowledgeBase/AddKnowledgeDocumentDialog.vue';
import EditKnowledgeDocumentDialog from 'dashboard/components-next/assistant/pageComponents/knowledgeBase/EditKnowledgeDocumentDialog.vue';
import KnowledgeBaseQaDialog from 'dashboard/components-next/assistant/pageComponents/knowledgeBase/KnowledgeBaseQaDialog.vue';

// Р9.3: страница базы знаний (Dify-стиль). Document-база — список документов
// (файлы/ссылки/тексты) с редактированием и переиндексацией; qa-база — пары
// «вопрос-ответ» с ревью (approved/pending).
const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { checkPermissions } = usePolicy();

// Р11 п.5: действия в строке таблицы доступны только администратору.
const canManage = computed(() => checkPermissions(['administrator']));

const knowledgeBaseId = computed(() => Number(route.params.knowledgeBaseId));

const backUrl = computed(() => ({
  name: 'assistant_knowledge_bases_index',
  params: { accountId: route.params.accountId },
}));

const getKnowledgeBase = useMapGetter('assistantKnowledgeBases/getRecord');
const knowledgeBase = computed(() =>
  getKnowledgeBase.value(knowledgeBaseId.value)
);
const isQaBase = computed(() => knowledgeBase.value?.kind === 'qa_base');

const documents = useMapGetter('assistantDocuments/getRecords');
const documentsMeta = useMapGetter('assistantDocuments/getMeta');
const documentsUiFlags = useMapGetter('assistantDocuments/getUIFlags');

const responses = useMapGetter('assistantResponses/getRecords');
const responsesMeta = useMapGetter('assistantResponses/getMeta');
const responsesUiFlags = useMapGetter('assistantResponses/getUIFlags');

const isReady = ref(false);
const statusFilter = ref('approved');

const isFetching = computed(() => {
  if (!isReady.value) return true;
  return isQaBase.value
    ? responsesUiFlags.value.fetchingList
    : documentsUiFlags.value.fetchingList;
});

const items = computed(() =>
  isQaBase.value ? responses.value : documents.value
);
const meta = computed(() =>
  isQaBase.value ? responsesMeta.value : documentsMeta.value
);

const fetchItems = (page = 1) => {
  if (isQaBase.value) {
    store.dispatch('assistantResponses/get', {
      page,
      knowledgeBaseId: knowledgeBaseId.value,
      status: statusFilter.value,
    });
  } else {
    store.dispatch('assistantDocuments/get', {
      page,
      knowledgeBaseId: knowledgeBaseId.value,
    });
  }
};

const onPageChange = page => fetchItems(page);

const refetchCurrentPage = () => {
  if (items.value?.length === 0 && meta.value?.page > 1) {
    fetchItems(meta.value.page - 1);
  } else {
    fetchItems(meta.value?.page || 1);
  }
};

const setStatusFilter = status => {
  if (statusFilter.value === status) return;
  statusFilter.value = status;
  fetchItems(1);
};

// --- Диалоги ---
const showEditDialog = ref(false);
const editKnowledgeBaseDialog = ref(null);

const showAddDialog = ref(false);
const addDialog = ref(null);

const selectedDocumentId = ref(null);
const showEditDocumentDialog = ref(false);
const editDocumentDialog = ref(null);

const selectedItem = ref(null);
const deleteItemDialog = ref(null);

const qaDialogType = ref('');
const qaDialog = ref(null);

const handleEditKnowledgeBase = () => {
  showEditDialog.value = true;
  nextTick(() => editKnowledgeBaseDialog.value.dialogRef.open());
};

const handleAdd = () => {
  if (isQaBase.value) {
    qaDialogType.value = 'create';
    selectedItem.value = null;
    nextTick(() => qaDialog.value.dialogRef.open());
  } else {
    showAddDialog.value = true;
    nextTick(() => addDialog.value.dialogRef.open());
  }
};

const handleEditDocument = id => {
  selectedDocumentId.value = id;
  showEditDocumentDialog.value = true;
  nextTick(() => editDocumentDialog.value.dialogRef.open());
};

const handleSyncDocument = async id => {
  try {
    await store.dispatch('assistantDocuments/sync', id);
    useAlert(t('ASSISTANT.DOCUMENTS.SYNC.QUEUED_MESSAGE'));
  } catch (error) {
    useAlert(t('ASSISTANT.DOCUMENTS.SYNC.ERROR_MESSAGE'));
  }
};

const handleDocumentAction = ({ action, id }) => {
  selectedItem.value = documents.value.find(document => id === document.id);
  nextTick(() => {
    if (action === 'edit') {
      handleEditDocument(id);
    } else if (action === 'sync') {
      handleSyncDocument(id);
    } else if (action === 'delete') {
      deleteItemDialog.value.dialogRef.open();
    }
  });
};

const handleApproveResponse = async id => {
  try {
    await store.dispatch('assistantResponses/update', {
      id,
      status: 'approved',
    });
    useAlert(t('ASSISTANT.KNOWLEDGE_BASES.QA.APPROVE_SUCCESS_MESSAGE'));
    refetchCurrentPage();
  } catch (error) {
    useAlert(t('ASSISTANT.KNOWLEDGE_BASES.QA.EDIT.ERROR_MESSAGE'));
  }
};

const handleResponseAction = ({ action, id }) => {
  selectedItem.value = responses.value.find(response => id === response.id);
  nextTick(() => {
    if (action === 'edit') {
      qaDialogType.value = 'edit';
      nextTick(() => qaDialog.value.dialogRef.open());
    } else if (action === 'approve') {
      handleApproveResponse(id);
    } else if (action === 'delete') {
      deleteItemDialog.value.dialogRef.open();
    }
  });
};

const handleQaDialogClose = () => {
  qaDialogType.value = '';
};

const onMutationSuccess = () => refetchCurrentPage();

// Р11 п.5: документы базы — компактной таблицей вместо карточек.
const isProcessing = status => status === 'in_progress';

const documentStatusLabel = status =>
  isProcessing(status)
    ? t('ASSISTANT.KNOWLEDGE_BASES.SHOW.DOCUMENT_STATUS.IN_PROGRESS')
    : t('ASSISTANT.KNOWLEDGE_BASES.SHOW.DOCUMENT_STATUS.AVAILABLE');

// Дата изменения — dd.MM.yyyy, HH:mm; прочерк, если сервер не прислал.
const formatUpdatedAt = ts =>
  ts ? messageStamp(ts, 'dd.MM.yyyy, HH:mm') : '—';

const documentLink = externalLink =>
  isSafeHttpLink(externalLink) ? getDocumentDisplayPath(externalLink) : '';

const emptyStateTitle = computed(() => {
  if (!isQaBase.value) {
    return t('ASSISTANT.KNOWLEDGE_BASES.SHOW.EMPTY_DOCUMENTS.TITLE');
  }
  return statusFilter.value === 'pending'
    ? t('ASSISTANT.KNOWLEDGE_BASES.SHOW.EMPTY_QA_PENDING.TITLE')
    : t('ASSISTANT.KNOWLEDGE_BASES.SHOW.EMPTY_QA.TITLE');
});

const emptyStateSubtitle = computed(() => {
  if (!isQaBase.value) {
    return t('ASSISTANT.KNOWLEDGE_BASES.SHOW.EMPTY_DOCUMENTS.SUBTITLE');
  }
  return statusFilter.value === 'pending'
    ? t('ASSISTANT.KNOWLEDGE_BASES.SHOW.EMPTY_QA_PENDING.SUBTITLE')
    : t('ASSISTANT.KNOWLEDGE_BASES.SHOW.EMPTY_QA.SUBTITLE');
});

onMounted(async () => {
  // Сначала узнаём тип базы, затем грузим её содержимое.
  await store.dispatch('assistantKnowledgeBases/show', knowledgeBaseId.value);
  isReady.value = true;
  fetchItems();
});
</script>

<template>
  <PageLayout
    :header-title="knowledgeBase?.name || ''"
    :button-label="
      isQaBase
        ? $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.ADD_QA')
        : $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.ADD_DOCUMENT')
    "
    :button-policy="['administrator']"
    :back-url="backUrl"
    :total-count="meta.totalCount"
    :current-page="meta.page"
    :is-fetching="isFetching"
    :is-empty="!items.length && statusFilter === 'approved'"
    :show-pagination-footer="!isFetching && !!items.length"
    :feature-flag="FEATURE_FLAGS.AI_ASSISTANT"
    @update:current-page="onPageChange"
    @click="handleAdd"
  >
    <template #search>
      <Button
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-pencil-line"
        :label="$t('ASSISTANT.KNOWLEDGE_BASES.EDIT.BUTTON')"
        @click="handleEditKnowledgeBase"
      />
    </template>

    <template #subHeader>
      <p
        v-if="knowledgeBase?.description"
        class="max-w-3xl pb-4 -mt-2 text-sm text-n-slate-11"
      >
        {{ knowledgeBase.description }}
      </p>
    </template>

    <template #controls>
      <div v-if="isQaBase" class="flex items-center gap-2 pb-4">
        <Button
          v-for="status in ['approved', 'pending']"
          :key="status"
          :label="
            $t(
              `ASSISTANT.KNOWLEDGE_BASES.SHOW.STATUS_FILTER.${status.toUpperCase()}`
            )
          "
          :variant="statusFilter === status ? 'solid' : 'faded'"
          :color="statusFilter === status ? 'blue' : 'slate'"
          size="sm"
          @click="setStatusFilter(status)"
        />
      </div>
    </template>

    <template #emptyState>
      <EmptyStateLayout
        :title="emptyStateTitle"
        :subtitle="emptyStateSubtitle"
        :action-perms="['administrator']"
      >
        <template #actions>
          <Button
            :label="
              isQaBase
                ? $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.ADD_QA')
                : $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.ADD_DOCUMENT')
            "
            icon="i-lucide-plus"
            @click="handleAdd"
          />
        </template>
      </EmptyStateLayout>
    </template>

    <template #body>
      <div
        v-if="!items.length"
        class="flex flex-col items-center justify-center min-h-80 gap-2 text-center"
      >
        <span class="text-base font-medium text-n-slate-12">
          {{ emptyStateTitle }}
        </span>
        <span class="max-w-md text-sm text-n-slate-11">
          {{ emptyStateSubtitle }}
        </span>
      </div>

      <div v-else-if="isQaBase" class="flex flex-col gap-4">
        <ResponseCard
          v-for="response in responses"
          :id="response.id"
          :key="response.id"
          :question="response.question"
          :answer="response.answer"
          :status="response.status"
          :created-at="response.created_at"
          :updated-at="response.updated_at"
          :show-actions="statusFilter === 'pending'"
          @action="handleResponseAction"
        />
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="text-left border-b text-n-slate-11 border-n-weak">
              <th class="py-2 pr-4 font-medium">
                {{ $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.TABLE.NAME') }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{ $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.TABLE.STATUS') }}
              </th>
              <th class="py-2 pr-4 font-medium whitespace-nowrap">
                {{ $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.TABLE.UPDATED') }}
              </th>
              <th
                v-if="canManage"
                class="py-2 font-medium text-right whitespace-nowrap"
              >
                {{ $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="document in documents"
              :key="document.id"
              class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
            >
              <td class="py-2.5 pr-4 align-middle max-w-xs">
                <div class="flex flex-col min-w-0 gap-0.5">
                  <span class="truncate text-n-slate-12">
                    {{ document.name || document.external_link }}
                  </span>
                  <a
                    v-if="documentLink(document.external_link)"
                    :href="document.external_link"
                    :title="document.external_link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 text-xs truncate text-n-slate-11 hover:text-n-slate-12 hover:underline"
                  >
                    <Icon icon="i-ph-link-simple" class="shrink-0" />
                    <span class="truncate">
                      {{ documentLink(document.external_link) }}
                    </span>
                  </a>
                </div>
              </td>
              <td class="py-2.5 pr-4 align-middle">
                <span
                  class="inline-flex items-center gap-1 whitespace-nowrap"
                  :class="
                    isProcessing(document.status)
                      ? 'text-n-amber-11'
                      : 'text-n-teal-11'
                  "
                >
                  <Icon
                    :icon="
                      isProcessing(document.status)
                        ? 'i-lucide-loader'
                        : 'i-lucide-check'
                    "
                    class="size-3.5"
                    :class="{ 'animate-spin': isProcessing(document.status) }"
                  />
                  {{ documentStatusLabel(document.status) }}
                </span>
              </td>
              <td
                class="py-2.5 pr-4 align-middle whitespace-nowrap text-n-slate-11"
              >
                {{ formatUpdatedAt(document.updated_at) }}
              </td>
              <td v-if="canManage" class="py-2.5 align-middle">
                <div class="flex items-center justify-end gap-1">
                  <Button
                    icon="i-lucide-pencil-line"
                    color="slate"
                    variant="ghost"
                    size="xs"
                    :title="$t('ASSISTANT.KNOWLEDGE_BASES.SHOW.OPTIONS.EDIT')"
                    :aria-label="
                      $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.OPTIONS.EDIT')
                    "
                    @click="
                      handleDocumentAction({ action: 'edit', id: document.id })
                    "
                  />
                  <Button
                    icon="i-lucide-refresh-cw"
                    color="slate"
                    variant="ghost"
                    size="xs"
                    :title="$t('ASSISTANT.KNOWLEDGE_BASES.SHOW.OPTIONS.SYNC')"
                    :aria-label="
                      $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.OPTIONS.SYNC')
                    "
                    @click="
                      handleDocumentAction({ action: 'sync', id: document.id })
                    "
                  />
                  <Button
                    icon="i-lucide-trash"
                    color="ruby"
                    variant="ghost"
                    size="xs"
                    :title="$t('ASSISTANT.KNOWLEDGE_BASES.SHOW.OPTIONS.DELETE')"
                    :aria-label="
                      $t('ASSISTANT.KNOWLEDGE_BASES.SHOW.OPTIONS.DELETE')
                    "
                    @click="
                      handleDocumentAction({
                        action: 'delete',
                        id: document.id,
                      })
                    "
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <CreateKnowledgeBaseDialog
      v-if="showEditDialog"
      ref="editKnowledgeBaseDialog"
      type="edit"
      :selected-knowledge-base="knowledgeBase"
      @close="showEditDialog = false"
    />

    <AddKnowledgeDocumentDialog
      v-if="showAddDialog"
      ref="addDialog"
      :knowledge-base-id="knowledgeBaseId"
      @create-success="onMutationSuccess"
      @close="showAddDialog = false"
    />

    <EditKnowledgeDocumentDialog
      v-if="showEditDocumentDialog"
      ref="editDocumentDialog"
      :document-id="selectedDocumentId"
      @update-success="onMutationSuccess"
      @close="showEditDocumentDialog = false"
    />

    <KnowledgeBaseQaDialog
      v-if="qaDialogType"
      ref="qaDialog"
      :type="qaDialogType"
      :knowledge-base-id="knowledgeBaseId"
      :selected-response="selectedItem"
      @success="onMutationSuccess"
      @close="handleQaDialogClose"
    />

    <DeleteDialog
      v-if="selectedItem"
      ref="deleteItemDialog"
      :entity="selectedItem"
      :type="isQaBase ? 'Responses' : 'Documents'"
      @delete-success="onMutationSuccess"
    />
  </PageLayout>
</template>
